// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable formatjs/no-literal-string-in-jsx */
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageNotFound } from "#/components/views/error/PageNotFound"
import { Title } from "#/services/page-title/Title"
import { Paths } from "#/utils/Paths"
import { reportErrorMessage } from "#/utils/ErrorReporting"
import { Alert, Box, Progress } from "@carrotfertility/carotene-core"
import {
  IntakeForm,
  PhotoIdUpload,
  SidebarAppointmentsTile,
  markAppointmentFormsComplete,
  useAppointmentFormsMarkedComplete,
  useTelemedicineAccess
} from "@internal/telemedicine"
import { useQueryClient } from "@tanstack/react-query"
import { TRPCClientError } from "@trpc/client"
import React from "react"
import { Redirect, useParams } from "react-router-dom"
import type { IntakeSubmittedLocationState } from "#/pages/telemedicine/IntakeSubmittedDialog"
import { useIntakeForms, removeCompletedIntakeForm } from "./hooks/useIntakeForms"
import { usePhotoIdOnFile } from "./hooks/usePhotoIdOnFile"
import { useSubmitIntakeForm } from "./hooks/useSubmitIntakeForm"
import { useUploadIdDocuments } from "./hooks/useUploadIdDocuments"

const errorMessage = (e: unknown): string => (e instanceof Error ? e.message : "unknown error")

function isNotFoundError(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) return false
  const data = (error as TRPCClientError<never>).data as { code?: string } | undefined
  return data?.code === "NOT_FOUND"
}

const CenteredProgress = () => (
  <Box
    sx={(theme) => ({
      marginBlockStart: theme.tokens.spacing.md,
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    })}
  >
    <Progress />
  </Box>
)

export function TelemedicineBookingFormsPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { data: access, isLoading, isError } = useTelemedicineAccess()
  const hasAccess = access != null && access.eligible && access.entry.status !== "single_unqualified"

  const intakeForms = useIntakeForms(id, hasAccess)
  const photoIdOnFile = usePhotoIdOnFile(hasAccess)
  const submit = useSubmitIntakeForm()
  const uploadIds = useUploadIdDocuments()
  const formsMarkedComplete = useAppointmentFormsMarkedComplete(id)

  if (isLoading) {
    return null
  }

  if (isError || !hasAccess) {
    return <Redirect to={Paths.HOME} />
  }

  // NOT_FOUND means the appointment isn't the member's or is in the past.
  if (intakeForms.error && isNotFoundError(intakeForms.error)) {
    return <PageNotFound />
  }

  const forms = intakeForms.data?.forms
  const submittedState: IntakeSubmittedLocationState = { intakeQuestionnaireSubmitted: true }
  const redirectToConfirmationModal = <Redirect to={{ pathname: Paths.HOME, state: submittedState }} />

  const allFormsDone = formsMarkedComplete || (forms != null && forms.length === 0)
  if (allFormsDone) {
    return intakeForms.data?.hasForms === false ? <PageNotFound /> : redirectToConfirmationModal
  }

  const renderQuestionnaire = (): JSX.Element => {
    const form = forms?.[0]
    if (!forms || !form) return <Alert severity="error">No intake form is connected to this appointment.</Alert>
    const isLastForm = forms.length === 1
    return (
      <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>
        <IntakeForm
          key={form.id}
          form={form}
          submitError={submit.isError}
          onError={reportErrorMessage}
          warnBeforeExit
          onSubmit={async (answers) => {
            await submit.mutateAsync({ appointmentId: id, formId: form.id, answers })
            if (isLastForm) markAppointmentFormsComplete(queryClient, id)
            removeCompletedIntakeForm(queryClient, id, form.id)
          }}
        />
      </Box>
    )
  }

  const renderIdStep = (): JSX.Element => {
    if (photoIdOnFile.isLoading) return <CenteredProgress />
    const idComplete = photoIdOnFile.data?.onFile === true || uploadIds.isSuccess
    if (!idComplete) {
      return (
        <>
          <PhotoIdUpload
            onSubmit={async (images) => {
              await uploadIds.mutateAsync(images)
            }}
            submitting={uploadIds.isPending}
          />
          {uploadIds.error ? <Alert severity="error">ID upload failed: {errorMessage(uploadIds.error)}</Alert> : null}
        </>
      )
    }
    return renderQuestionnaire()
  }

  const renderContent = (): JSX.Element => {
    if (intakeForms.isLoading) return <CenteredProgress />

    if (intakeForms.error) {
      return <Alert severity="error">Something went wrong loading these questions. Please try again.</Alert>
    }

    return renderIdStep()
  }

  return (
    <PageLayout
      variant="sidebar"
      sidebar={<SidebarAppointmentsTile appointmentsPath={Paths.TELEMEDICINE_APPOINTMENTS} />}
    >
      <Title title="Telemedicine Intake Form" />
      {renderContent()}
    </PageLayout>
  )
}
